import React, { useContext, createContext, useState } from "react";
import { Account } from "services/api/auth/types";

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
	const [auth, setAuth] = useState<Account>(() => {
		const cachedAuth = localStorage.getItem("auth");
		if (!cachedAuth || cachedAuth === "undefined") return null;
		try {
			return JSON.parse(cachedAuth);
		} catch (error) {
			console.error("Failed to parse cached auth:", error);
			return null;
		}
	});
	const [isOfflineSession, setIsOfflineSession] = useState(false);
	const [userId, setUserId] = useState(() => {
		const cachedUser = localStorage.getItem("userInfo");
		if (!cachedUser || cachedUser === "undefined") return "";
		try {
			const parsed = JSON.parse(cachedUser);
			return parsed.id || "";
		} catch (error) {
			return "";
		}
	});
	const [eventId, setEventId] = useState(() => {
		const cachedUser = localStorage.getItem("userInfo");
		if (!cachedUser || cachedUser === "undefined") return "";
		try {
			const parsed = JSON.parse(cachedUser);
			return parsed.eventId || "";
		} catch (error) {
			return "";
		}
	});
	const [userName, setUserName] = useState(() => {
		const cachedUser = localStorage.getItem("userInfo");
		if (!cachedUser || cachedUser === "undefined") return "";
		try {
			const parsed = JSON.parse(cachedUser);
			return parsed.name || "";
		} catch (error) {
			return "";
		}
	});

	const isAuth: boolean = React.useMemo(() => !!auth, [auth]);

	const getToken = () => {
		return auth?.token;
	};

	const getAccountData = (): Account => {
		return auth;
	};

	const resumeOfflineSession = (cachedUser: any) => {
		if (!cachedUser) return;

		// Create a minimal auth object for offline session
		setAuth({
			token: "offline-session-token", // Placeholder token for offline mode
			eventList: [],
			userId: cachedUser.id || "",
			userName: cachedUser.name || "",
		});

		// Set user info
		setUserId(cachedUser.id || "");
		setUserName(cachedUser.name || "");
		if (cachedUser.eventId) {
			setEventId(cachedUser.eventId);
		}

		setIsOfflineSession(true);
	};

	const getLastCachedUser = () => {
		const cachedUser = localStorage.getItem("userInfo");
		if (!cachedUser || cachedUser === "undefined") return null;
		try {
			return JSON.parse(cachedUser);
		} catch (error) {
			console.error("Failed to parse cached user:", error);
			return null;
		}
	};

	const setUserID = (userId: string) => {
		setUserId(userId);
	};

	const getUserId = () => {
		return userId;
	};

	const setEventIdValue = (eventId: string) => {
		setEventId(eventId);
	};

	const getEventIdValue = () => {
		return eventId;
	};

	const setUserNameValue = (userName: string) => {
		setUserName(userName);
	};

	const getUserNameValue = () => {
		return JSON.parse(localStorage.getItem("userInfo") || "{}").name;
	};

	const getUserEmailValue = (): string => {
		try {
			const raw = localStorage.getItem("userInfo");
			if (!raw || raw === "undefined") return "";
			const parsed = JSON.parse(raw);
			return typeof parsed?.email === "string" ? parsed.email : "";
		} catch {
			return "";
		}
	};

	const login = (userData) => {
		setIsOfflineSession(false);
		setAuth(userData);
		localStorage.setItem("auth", JSON.stringify(userData));
	};

	/**
	 * C1/C4: Full logout - clears all data including cached user info
	 * Use this for actual user sign-out
	 */
	const logout = () => {
		setAuth(null);
		localStorage.removeItem("auth");
		localStorage.removeItem("userInfo"); // C1/C4: Clear cached user for sign out
		setIsOfflineSession(false);
		setUserId("");
		setUserName("");
		setEventId("");
	};

	/**
	 * Clear auth token only, keep userInfo for offline session resumption
	 * Use this for testing offline resume scenarios (not for actual logout)
	 */
	const clearAuthToken = () => {
		setAuth(null);
		localStorage.removeItem("auth");
		// Keep userInfo for A4 scenario (offline session resumption)
		setIsOfflineSession(false);
		setUserId("");
		setUserName("");
		setEventId("");
	};

	const method = {
		isAuth,
		isOfflineSession,
		getAccountData,
		getToken,
		login,
		logout,
		clearAuthToken,
		resumeOfflineSession,
		getLastCachedUser,
		setUserID,
		getUserId,
		setUserNameValue,
		getUserNameValue,
		getUserEmailValue,
		setEventIdValue,
		getEventIdValue,
	};

	return <AuthContext.Provider value={method}>{children}</AuthContext.Provider>;
};

export default AuthProvider;

export const useAuth = () => {
	return useContext(AuthContext);
};
